<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  inventoryApi, type ExitCause, type InventoryBalance, type InventoryItem, type InventoryUnit, type MovementForm,
  type PlaceFilter,
} from '@/api/inventory';
import { patientsApi } from '@/api/patients';
import { apiErrorText } from '@/utils/api-errors';
import { todayIso } from '@/utils/calendar-events';
import { EXIT_CAUSES, formatMoney, formatQuantity } from '@/utils/inventory';
import PriceInput from '@/components/price-input.vue';

// One dialog for every movement recorded from a place: a patient's home
// (`place.patientId`) or a warehouse (`place.locationId`). `mode` picks the
// action; `balance` or `unit` preselects what moves.
export type MovementMode = 'entry' | 'use' | 'count' | 'transfer' | 'loss' | 'return' | 'delivery';

const props = defineProps<{
  place: PlaceFilter;
  mode: MovementMode;
  balance?: InventoryBalance | null;
  unit?: InventoryUnit | null;
}>();
const emit = defineEmits<{ close: [] }>();

const TITLES: Record<MovementMode, string> = {
  entry: 'Registrar ingreso',
  use: 'Registrar uso',
  count: 'Ajustar por conteo',
  transfer: 'Trasladar',
  loss: 'Registrar pérdida o baja',
  return: 'Devolver',
  delivery: 'Entregar a un paciente',
};

const atHome = !!props.place.patientId;

const queryClient = useQueryClient();
const error = ref('');
const form = reactive({
  itemId: props.balance?.inventoryItemId ?? props.unit?.inventoryItemId,
  unitId: props.unit?.id,
  supplierId: props.mode === 'return' ? props.unit?.ownerLocationId ?? undefined : undefined,
  destinationPatientId: undefined as number | undefined,
  destinationLocationId: undefined as number | undefined,
  quantity: undefined as number | undefined,
  countedQuantity: undefined as number | undefined,
  totalCostCents: 0,
  lotCode: '',
  expiresOn: '',
  identifier: '',
  ownerLocationId: undefined as number | undefined,
  exitCause: undefined as ExitCause | undefined,
  occurredOn: todayIso(),
  description: '',
  minQuantity: undefined as number | '' | undefined,
});

const { data: items } = useQuery({
  queryKey: ['inventoryItems', 'active'],
  queryFn: () => inventoryApi.items({ scope: 'active' }),
  initialData: [],
  enabled: props.mode === 'entry',
});
const { data: suppliers } = useQuery({
  queryKey: ['inventoryLocations', 'suppliers'],
  queryFn: () => inventoryApi.locations({ kinds: ['supplier'] }),
  initialData: [],
  enabled: props.mode === 'entry' || props.mode === 'return',
});
const { data: warehouses } = useQuery({
  queryKey: ['inventoryLocations', 'warehouses'],
  queryFn: () => inventoryApi.locations({ kinds: ['warehouse'] }),
  initialData: [],
  enabled: props.mode === 'return' || (props.mode === 'transfer' && !atHome),
});
const { data: patients } = useQuery({
  queryKey: ['patientsForFilters'],
  queryFn: () => patientsApi.indexForFilters(),
  initialData: [],
  enabled: (props.mode === 'transfer' && atHome) || props.mode === 'delivery',
});

const selectedItem = computed<InventoryItem | undefined>(() => items.value.find((item) => item.id === form.itemId));
const serialized = computed(() => (
  props.unit ? true : selectedItem.value?.tracking === 'serialized'
));
const destinations = computed(() => patients.value.filter((patient) => patient.id !== props.place.patientId));
const otherWarehouses = computed(() => warehouses.value.filter((warehouse) => warehouse.id !== props.place.locationId));
// From a home stock goes back to a supplier or to a warehouse; from a warehouse, to a supplier.
const returnTargets = computed(() => (atHome ? [...suppliers.value, ...warehouses.value] : suppliers.value));
const available = computed(() => props.balance?.quantity);
// The entry cost is the total paid for everything entered; show what it means per unit.
const costHint = computed(() => {
  if (serialized.value || !form.quantity || form.quantity <= 0 || !form.totalCostCents) {
    return 'Total pagado por todo lo que ingresa (0 si no tiene costo para la empresa).';
  }
  const perUnit = formatMoney(Math.round(form.totalCostCents / form.quantity), selectedItem.value?.currency);

  return `Total de las ${formatQuantity(form.quantity)} unidades · ≈ ${perUnit} por unidad.`;
});
const currentStock = computed(() => (
  props.balance ? `${formatQuantity(props.balance.quantity)} ${props.balance.humanUnitOfMeasure.toLowerCase()}` : ''
));

// This place as one end of the movement: `fromPatientId`, `toLocationId`, `patientId`…
function here(prefix: 'from' | 'to' | ''): Partial<MovementForm> {
  const key = atHome ? 'PatientId' : 'LocationId';
  const id = atHome ? props.place.patientId : props.place.locationId;
  const name = prefix ? `${prefix}${key}` : `${key.charAt(0).toLowerCase()}${key.slice(1)}`;

  return { [name]: id };
}

function transferTarget(): Partial<MovementForm> {
  return atHome ? { toPatientId: form.destinationPatientId } : { toLocationId: form.destinationLocationId };
}

function locationFields(): Partial<MovementForm> {
  switch (props.mode) {
  case 'entry': return { fromLocationId: form.supplierId, ...here('to') };
  case 'count': return here('');
  case 'transfer': return { ...here('from'), ...transferTarget() };
  case 'return': return { ...here('from'), toLocationId: form.supplierId };
  case 'delivery': return { ...here('from'), toPatientId: form.destinationPatientId };
  default: return here('from');
  }
}

const REASONS: Record<MovementMode, MovementForm['reason']> = {
  entry: 'entry',
  use: 'use',
  count: 'count_adjustment',
  transfer: 'transfer',
  loss: 'loss',
  return: 'return',
  delivery: 'delivery',
};

function reasonFor(mode: MovementMode): MovementForm['reason'] {
  return REASONS[mode];
}

function entryFields(): Partial<MovementForm> {
  if (props.mode !== 'entry') return {};
  if (serialized.value) {
    return {
      totalCostCents: form.totalCostCents,
      newUnit: { identifier: form.identifier || undefined, ownerLocationId: form.ownerLocationId },
    };
  }

  const lot = form.lotCode || form.expiresOn ? { code: form.lotCode, expiresOn: form.expiresOn } : undefined;

  return { totalCostCents: form.totalCostCents, lot };
}

const { data: systemLocations } = useQuery({
  queryKey: ['inventoryLocations', 'system'],
  queryFn: () => inventoryApi.locations({ kinds: ['consumed', 'loss'] }),
  initialData: [],
  enabled: props.mode === 'use' || props.mode === 'loss',
});

function sinkFields(): Partial<MovementForm> {
  const kind = props.mode === 'use' ? 'consumed' : 'loss';
  if (props.mode !== 'use' && props.mode !== 'loss') return {};

  return { toLocationId: systemLocations.value.find((location) => location.kind === kind)?.id };
}

function payload(): MovementForm {
  return {
    reason: reasonFor(props.mode),
    inventoryItemId: form.itemId as number,
    quantity: serialized.value ? undefined : form.quantity,
    countedQuantity: props.mode === 'count' ? form.countedQuantity : undefined,
    inventoryUnitId: props.mode === 'entry' ? undefined : form.unitId,
    exitCause: props.mode === 'loss' ? form.exitCause : undefined,
    occurredOn: form.occurredOn,
    description: form.description || undefined,
    ...locationFields(),
    ...sinkFields(),
    ...entryFields(),
  };
}

// An entry may also set the item's minimum at this place (blank keeps the
// current one). The movement is recorded once: retrying after a failed
// minimum only retries the minimum.
class MinimumNotSaved extends Error {}

const recorded = ref(false);
// `v-model.number` leaves '' once the field is cleared: only a number sets it.
const setsMinimum = computed(() => (
  props.mode === 'entry' && !serialized.value && typeof form.minQuantity === 'number'
));

function refreshLists() {
  ['inventoryBalances', 'inventoryUnits', 'inventoryMovements', 'inventoryAlerts']
    .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
}

async function submit(): Promise<void> {
  if (!recorded.value) {
    await inventoryApi.recordMovement(payload());
    recorded.value = true;
    refreshLists();
  }
  if (!setsMinimum.value) return;

  try {
    await inventoryApi.setMinimum({
      inventoryItemId: form.itemId as number, ...props.place, minQuantity: form.minQuantity as number,
    });
  } catch (failure) {
    throw new MinimumNotSaved(apiErrorText(failure, 'No se pudo guardar el mínimo.'));
  }
  refreshLists();
}

const save = useMutation({
  mutationFn: submit,
  onSuccess: () => emit('close'),
  onError: (failure) => {
    error.value = failure instanceof MinimumNotSaved ?
      `El ingreso quedó registrado, pero no el mínimo: ${failure.message}` :
      apiErrorText(failure, 'No se pudo registrar el movimiento.');
  },
});

// What each mode needs besides the item and a positive quantity (or a unit).
const MODE_READY: Record<MovementMode, () => boolean> = {
  entry: () => !!form.supplierId,
  use: () => true,
  count: () => true,
  transfer: () => !!(atHome ? form.destinationPatientId : form.destinationLocationId),
  loss: () => !!form.exitCause,
  return: () => !!form.supplierId,
  delivery: () => !!form.destinationPatientId,
};

const hasQuantity = computed(() => {
  if (props.mode === 'count') return form.countedQuantity !== undefined && form.countedQuantity >= 0;

  return serialized.value || (form.quantity !== undefined && form.quantity > 0);
});

const valid = computed(() => !!form.itemId && MODE_READY[props.mode]() && hasQuantity.value);
</script>
<template>
  <v-card :title="TITLES[mode]">
    <v-card-text class="pt-5">
      <v-alert
        v-if="balance || unit"
        variant="tonal"
        color="primary"
        density="compact"
        class="mb-4"
      >
        <b>{{ balance?.itemName ?? unit?.itemName }}</b>
        <span v-if="unit?.identifier"> · {{ unit.identifier }}</span>
        <span v-if="balance"> · stock actual {{ currentStock }}</span>
      </v-alert>

      <template v-if="mode === 'entry'">
        <div class="text-subtitle-2 font-weight-bold mb-3">
          Origen y artículo
        </div>
        <v-select
          v-model="form.supplierId"
          :items="suppliers"
          item-title="name"
          item-value="id"
          label="Proviene de"
          variant="outlined"
          density="compact"
          hint="Se configuran en Inventario › Configuración"
          persistent-hint
          class="mb-3"
        />
        <v-autocomplete
          v-model="form.itemId"
          :items="items"
          item-title="name"
          item-value="id"
          label="Artículo"
          variant="outlined"
          density="compact"
        />
        <v-row
          v-if="serialized"
          dense
        >
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="form.identifier"
              label="Identificador o n° de serie (opcional)"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-select
              v-model="form.ownerLocationId"
              :items="suppliers"
              item-title="name"
              item-value="id"
              label="Dueño (vacío = la empresa)"
              variant="outlined"
              density="compact"
              clearable
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
      </template>

      <template v-if="mode === 'transfer' || mode === 'return' || mode === 'loss' || mode === 'delivery'">
        <div class="text-subtitle-2 font-weight-bold mb-3">
          {{ mode === 'loss' ? 'Causa' : 'Destino' }}
        </div>
        <v-autocomplete
          v-if="(mode === 'transfer' && atHome) || mode === 'delivery'"
          v-model="form.destinationPatientId"
          :items="destinations"
          item-title="fullName"
          item-value="id"
          label="Paciente de destino"
          variant="outlined"
          density="compact"
        />
        <v-select
          v-if="mode === 'transfer' && !atHome"
          v-model="form.destinationLocationId"
          :items="otherWarehouses"
          item-title="name"
          item-value="id"
          label="Bodega de destino"
          variant="outlined"
          density="compact"
        />
        <v-select
          v-if="mode === 'return'"
          v-model="form.supplierId"
          :items="returnTargets"
          item-title="name"
          item-value="id"
          label="Devolver a"
          variant="outlined"
          density="compact"
        />
        <v-select
          v-if="mode === 'loss'"
          v-model="form.exitCause"
          :items="EXIT_CAUSES"
          label="Causa"
          variant="outlined"
          density="compact"
        />
        <v-divider class="my-4" />
      </template>

      <div class="text-subtitle-2 font-weight-bold mb-3">
        {{ mode === 'entry' ? 'Cantidad y costo' : 'Cantidad' }}
      </div>
      <v-row dense>
        <v-col
          v-if="!serialized && mode !== 'count'"
          cols="12"
          :sm="mode === 'entry' ? 6 : 12"
        >
          <v-text-field
            v-model.number="form.quantity"
            type="number"
            min="0"
            :step="selectedItem?.quantityStep ?? balance?.quantityStep ?? 1"
            :label="`Cantidad${selectedItem ? ` (${selectedItem.humanUnitOfMeasure.toLowerCase()})` : ''}`"
            :hint="available !== undefined && mode !== 'entry' ? `Disponible: ${formatQuantity(available)}` : undefined"
            persistent-hint
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          v-if="mode === 'count'"
          cols="12"
        >
          <v-text-field
            v-model.number="form.countedQuantity"
            type="number"
            min="0"
            label="Cantidad contada"
            hint="Lo que hay físicamente; el sistema calcula la diferencia"
            persistent-hint
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          v-if="mode === 'entry'"
          cols="12"
          :sm="serialized ? 12 : 6"
        >
          <price-input
            v-model="form.totalCostCents"
            label="Costo total del ingreso"
            :hint="costHint"
            density="compact"
          />
        </v-col>
      </v-row>
      <template v-if="mode === 'entry' && !serialized">
        <v-row dense>
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="form.lotCode"
              label="Lote (opcional)"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="form.expiresOn"
              type="date"
              label="Vencimiento (opcional)"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
        <v-text-field
          v-model.number="form.minQuantity"
          type="number"
          min="0"
          label="Stock mínimo aquí (opcional)"
          hint="Avisa por correo al llegar a este stock."
          persistent-hint
          variant="outlined"
          density="compact"
        />
      </template>

      <v-divider class="my-4" />
      <div class="text-subtitle-2 font-weight-bold mb-3">
        Registro
      </div>
      <v-row dense>
        <v-col
          cols="12"
          sm="5"
        >
          <v-text-field
            v-model="form.occurredOn"
            type="date"
            :max="todayIso()"
            label="Fecha"
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          cols="12"
          sm="7"
        >
          <v-text-field
            v-model="form.description"
            label="Observación (opcional)"
            variant="outlined"
            density="compact"
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!valid"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        Registrar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
